import React from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { RATIO_LABELS, type RatioType } from '../../../types/dashboard-widgets';
import { ResponsiveAmount } from '../../ui/ResponsiveAmount';

interface RatioWidgetProps {
  config: {
    ratioType?: RatioType;
    ratio?: RatioType;
    showInterpretation?: boolean;
  };
  data: any;
}

// Seuils et interprétations pour chaque ratio (aligné sur RatioType officiel)
const RATIO_THRESHOLDS: Record<RatioType, {
  good: number;
  warning: number;
  interpretation: (value: number) => string;
}> = {
  current_ratio: {
    good: 1.5,
    warning: 1.0,
    interpretation: (v) => v >= 1.5 ? 'Bonne liquidité' : v >= 1.0 ? 'Liquidité acceptable' : 'Liquidité insuffisante'
  },
  debt_equity_ratio: {
    good: 0.5,
    warning: 1.5,
    interpretation: (v) => v <= 0.5 ? 'Faible endettement' : v <= 1.5 ? 'Endettement modéré' : 'Endettement élevé'
  },
  gross_profit_margin: {
    good: 30,
    warning: 15,
    interpretation: (v) => v >= 30 ? 'Excellente marge brute' : v >= 15 ? 'Marge brute correcte' : 'Marge brute faible'
  },
  net_profit_margin: {
    good: 10,
    warning: 5,
    interpretation: (v) => v >= 10 ? 'Excellente rentabilité nette' : v >= 5 ? 'Rentabilité correcte' : 'Rentabilité faible'
  },
  working_capital_ratio: {
    good: 0.15,
    warning: 0.30,
    interpretation: (v) => v <= 0.15 ? 'BFR maîtrisé' : v <= 0.30 ? 'BFR acceptable' : 'BFR trop élevé'
  }
};

const DEFAULT_THRESHOLDS = {
  good: 1,
  warning: 0.5,
  interpretation: (_v: number) => 'Donnée disponible'
};

const getRatioValue = (data: any, ratioType: RatioType): number => {
  if (!data?.financialPosition?.ratios) return 0;
  
  const ratios = data.financialPosition.ratios;
  const paths: Record<RatioType, () => number> = {
    current_ratio: () => ratios.currentRatio ?? 0,
    debt_equity_ratio: () => ratios.debtEquityRatio ?? 0,
    gross_profit_margin: () => data.profitAndLoss?.current?.grossProfitMargin ?? 0,
    net_profit_margin: () => data.profitAndLoss?.current?.profitMargin ?? ratios.netProfitMargin ?? 0,
    working_capital_ratio: () => ratios.workingCapitalRatio ?? 0
  };
  
  return paths[ratioType]?.() ?? 0;
};

export function RatioWidget({ config, data }: RatioWidgetProps) {
  const ratioType = config.ratioType || config.ratio || 'current_ratio';
  const ratioInfo = RATIO_LABELS[ratioType] ?? { label: ratioType, benchmark: 0, unit: '' };
  const thresholds = RATIO_THRESHOLDS[ratioType] ?? DEFAULT_THRESHOLDS;
  const value = getRatioValue(data, ratioType);

  // Determine status
  const isInverted = ['debt_equity_ratio', 'working_capital_ratio'].includes(ratioType);
  const isPercent = ['gross_profit_margin', 'net_profit_margin'].includes(ratioType);
  
  let status: 'good' | 'warning' | 'danger';
  if (isInverted) {
    status = value <= thresholds.good ? 'good' : value <= thresholds.warning ? 'warning' : 'danger';
  } else {
    status = value >= thresholds.good ? 'good' : value >= thresholds.warning ? 'warning' : 'danger';
  }

  const statusColors = {
    good: 'text-green-600 dark:text-green-400',
    warning: 'text-orange-600 dark:text-orange-400',
    danger: 'text-red-600 dark:text-red-400'
  };

  const statusBgColors = {
    good: 'bg-green-100 dark:bg-green-900/30',
    warning: 'bg-orange-100 dark:bg-orange-900/30',
    danger: 'bg-red-100 dark:bg-red-900/30'
  };

  const StatusIcon = status === 'good' ? CheckCircle2 : status === 'warning' ? AlertCircle : AlertCircle;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground uppercase tracking-wide truncate">{ratioInfo.label}</p>
          <div className="mt-1">
            {isPercent ? (
              <span className={`text-2xl font-bold tabular-nums ${statusColors[status]}`}>
                {value.toFixed(1)}%
              </span>
            ) : (
              <span className={`text-2xl font-bold tabular-nums ${statusColors[status]}`}>
                {value.toFixed(2)}
              </span>
            )}
          </div>
        </div>
        <div className={`p-2 rounded-lg flex-shrink-0 ${statusBgColors[status]}`}>
          <StatusIcon className={`h-5 w-5 ${statusColors[status]}`} />
        </div>
      </div>

      {/* Interpretation */}
      {config.showInterpretation !== false && (
        <div className={`p-2 rounded-md ${statusBgColors[status]} border border-current/10`}>
          <p className={`text-xs font-medium ${statusColors[status]}`}>
            {thresholds.interpretation(value)}
          </p>
        </div>
      )}

      {/* Visual indicator */}
      <div className="pt-2">
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
          <span>Min</span>
          <span>Optimal</span>
          <span>Max</span>
        </div>
        <div className="relative h-2 bg-muted rounded-full overflow-hidden">
          {/* Gradient background */}
          <div className="absolute inset-0 bg-gradient-to-r from-red-400 via-yellow-400 to-green-400 opacity-30" />
          
          {/* Value indicator */}
          <div 
            className={`absolute top-0 bottom-0 w-1 ${statusColors[status].replace('text-', 'bg-').replace('dark:', '')}`}
            style={{ 
              left: `${Math.min(100, Math.max(0, isInverted 
                ? (1 - value / (thresholds.warning * 2)) * 100 
                : (value / (thresholds.good * 2)) * 100
              ))}%` 
            }}
          />
        </div>
      </div>
    </div>
  );
}
